import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../lib/auth";

/**
 * Bulk-import hires. Sibling to /settings/bulk-import (machines), but a
 * hire-level import: machine + customer + start + rate on one row. Missing
 * customers and machines are created in-flight — the preview screen calls
 * out exactly what's about to be created so Todd sees it before committing.
 */
export default function BulkImportHires() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role !== "Director") nav("/settings", { replace: true });
  }, [user, nav]);

  const downloadTemplate = async () => {
    setErr("");
    try {
      const r = await api.get("/settings/import-hires/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ach-hire-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || text; } catch { detail = text; }
        } catch { /* ignore */ }
      } else if (e.response?.data?.detail) {
        detail = formatApiErrorDetail(e.response.data.detail);
      }
      setErr(`Couldn't download the template — ${detail}`);
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setPreview(null); setResult(null);
    try {
      const text = await file.text();
      const r = await api.post("/settings/import-hires/preview", text, { headers: { "Content-Type": "text/csv" }});
      setPreview(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true); setErr("");
    try {
      const text = await file.text();
      const r = await api.post("/settings/import-hires/commit", text, { headers: { "Content-Type": "text/csv" }});
      setResult(r.data); setPreview(null); setFile(null);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCommitting(false); }
  };

  const kindLabel = (k) => ({
    "ready":                "Ready — machine & customer already on file",
    "will-create-machine":  "Will create a new machine",
    "will-create-customer": "Will create a new customer",
    "will-create-both":     "Will create both a new customer AND a new machine",
    "needs-review":         "Needs review before import",
  })[k] || k;

  const kindColour = (k) => ({
    "ready":                "var(--ach-green)",
    "will-create-machine":  "var(--ach-blue)",
    "will-create-customer": "var(--ach-blue)",
    "will-create-both":     "var(--ach-blue)",
    "needs-review":         "var(--ach-red)",
  })[k] || "var(--ach-text-2)";

  return (
    <div data-testid="bulk-import-hires-page">
      <PageHeader
        title="Bulk import hires"
        subtitle="One row per active hire — machine, customer, dates & weekly rate together"
        actions={<button className="ach-btn ach-btn--grey" onClick={()=>nav("/settings")} data-testid="bih-back">← Back to Settings</button>}
      />

      <div className="ach-card" style={{marginBottom: 16}}>
        <h2 className="ach-card__title">How this works</h2>
        <ol style={{margin:0, paddingLeft:20, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.7}}>
          <li>Download the template — columns match how the hires get tracked in your spreadsheet: <em>Entity, Unit No., Model, Serial No., Capacity, Customer, Hire Start date, Hire End date, Weekly Rate</em>.</li>
          <li>Fill one row per active hire. If a customer or a machine isn&apos;t already in the system, that&apos;s fine — this importer will create them for you as it goes.</li>
          <li>Save as CSV and upload. The preview screen shows what will be created before anything happens.</li>
          <li>Review, then press <em>Import</em>. Rows that need attention are flagged for you.</li>
        </ol>
        <div style={{marginTop:14, padding:12, background:"var(--ach-page-bg)", border:"1px dashed var(--ach-card-border)", borderRadius:8, fontSize:12, color:"var(--ach-text-2)", lineHeight:1.6}}>
          <strong>A few real-world notes:</strong>
          <ul style={{margin:"6px 0 0", paddingLeft:18}}>
            <li>Date formats are forgiving — <code>13/12/2026</code>, <code>13-12-2026</code>, or <code>2026-12-13</code> all work. Same with 2-digit years.</li>
            <li>Use <code>TBA</code> in the Hire End date column for open-ended hires. It&apos;ll be stored as a continuous hire.</li>
            <li>Monthly Rate is derived (weekly × 4.33) — don&apos;t send it as a column, we compute it consistently everywhere else in the app.</li>
            <li><strong>Entity codes matter.</strong> They&apos;re matched exactly against the known owning companies — <code>HYJA</code> and <code>HYJAC</code> aren&apos;t assumed to be the same. If a row&apos;s entity doesn&apos;t match, it&apos;s flagged for you to confirm.</li>
          </ul>
        </div>
        <button className="ach-btn ach-btn--primary" style={{marginTop:14}} onClick={downloadTemplate} data-testid="bih-download-template">↓ Download the template</button>
        {err && err.startsWith("Couldn't download the template") && (
          <div data-testid="bih-download-err" style={{marginTop:12, color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, fontSize:13}}>{err}</div>
        )}
      </div>

      <div className="ach-card" style={{marginBottom: 16}}>
        <h2 className="ach-card__title">Upload a filled-in file</h2>
        <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
          <input type="file" accept=".csv,text/csv"
            onChange={(e)=>{ setFile(e.target.files[0]); setPreview(null); setResult(null); setErr(""); }}
            data-testid="bih-file" />
          <button className="ach-btn ach-btn--primary" onClick={runPreview} disabled={!file || busy} data-testid="bih-preview">
            {busy ? "Checking…" : "Check the file →"}
          </button>
        </div>
        {err && !err.startsWith("Couldn't download the template") && (
          <div style={{marginTop:12, color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, fontSize:13}}>{err}</div>
        )}
      </div>

      {preview && (
        <div className="ach-card" data-testid="bih-preview-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, gap:12, flexWrap:"wrap"}}>
            <div>
              <h2 className="ach-card__title" style={{margin:0}}>Preview — nothing has been imported yet</h2>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:4, lineHeight:1.7}}>
                {preview.total} rows in the file. Of those:
                <br />
                <strong style={{color:"var(--ach-green)"}}>{preview.counts["ready"] || 0}</strong> ready to import ·
                <strong style={{color:"var(--ach-blue)"}}> {preview.counts["will-create-machine"] || 0}</strong> will create a new machine ·
                <strong style={{color:"var(--ach-blue)"}}> {preview.counts["will-create-customer"] || 0}</strong> will create a new customer ·
                <strong style={{color:"var(--ach-blue)"}}> {preview.counts["will-create-both"] || 0}</strong> will create both ·
                <strong style={{color:"var(--ach-red)"}}> {preview.needs_review}</strong> need review
              </div>
            </div>
            {preview.importable > 0 && (
              <button className="ach-btn ach-btn--primary" onClick={commit} disabled={committing} data-testid="bih-commit">
                {committing ? "Importing…" : `Import the ${preview.importable} good row${preview.importable === 1 ? "" : "s"} →`}
              </button>
            )}
          </div>
          <div className="ach-table-wrap">
            <table className="ach-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Unit</th>
                  <th>Customer</th>
                  <th>Entity</th>
                  <th>Dates</th>
                  <th>Weekly $</th>
                  <th>Plan</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.row_number} data-testid={`bih-row-${r.row_number}`}
                      style={r.kind === "needs-review" ? {background:"var(--ach-red-tint)"} : undefined}>
                    <td>{r.row_number}</td>
                    <td>{r.resolved.unit_number || <em>—</em>}</td>
                    <td>{r.resolved.customer_name || <em>—</em>}</td>
                    <td>
                      {r.resolved.owning_company
                        ? (r.resolved.is_external_cross_hired
                            ? <span style={{background:"var(--ach-amber-tint)", color:"var(--ach-amber)", padding:"2px 6px", borderRadius:4, fontSize:11, fontWeight:600}}>EXTERNAL</span>
                            : r.resolved.owning_company)
                        : <em style={{color:"var(--ach-red)"}}>{r.raw.entity || "—"}</em>}
                    </td>
                    <td style={{fontSize:12}}>
                      {r.resolved.start_date || <em>?</em>}
                      {" → "}
                      {r.resolved.continuous_hire ? <em>TBA</em> : (r.resolved.end_date || <em>?</em>)}
                    </td>
                    <td className="money">{r.resolved.weekly_rate != null ? `$${r.resolved.weekly_rate.toLocaleString()}` : <em>—</em>}</td>
                    <td>
                      <div style={{color: kindColour(r.kind), fontWeight:600, fontSize:12}}>
                        {kindLabel(r.kind)}
                      </div>
                      {r.notes.length > 0 && (
                        <ul style={{margin:"4px 0 0", paddingLeft:18, color:"var(--ach-blue)", fontSize:11}}>
                          {r.notes.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      )}
                      {r.errors.length > 0 && (
                        <ul style={{margin:"4px 0 0", paddingLeft:18, color:"var(--ach-red)", fontSize:11}}>
                          {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="ach-card" data-testid="bih-result-card" style={{background:"var(--ach-green-tint)", borderLeft:"4px solid var(--ach-green)"}}>
          <h2 className="ach-card__title" style={{margin:0}}>{result.message}</h2>
          <div style={{marginTop:10, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.7}}>
            <strong>Created:</strong> {result.hires_created} hire{result.hires_created===1?"":"s"},
            {" "}{result.customers_created} customer{result.customers_created===1?"":"s"},
            {" "}{result.machines_created} machine{result.machines_created===1?"":"s"}.
          </div>
          {result.skipped_count > 0 && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:13, color:"var(--ach-red)", fontWeight:600, marginBottom:6}}>Skipped rows — fix these and re-upload:</div>
              <ul style={{margin:0, paddingLeft:20, fontSize:12, color:"var(--ach-text-1)", lineHeight:1.6}}>
                {result.skipped.map(s => (
                  <li key={s.row_number}>Row {s.row_number} ({s.unit_number || "—"}): {s.errors.join(", ")}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{display:"flex", gap:10, marginTop:14, flexWrap:"wrap"}}>
            <button className="ach-btn ach-btn--primary" onClick={()=>nav("/hires")} data-testid="bih-view-hires">See the hires →</button>
            <button className="ach-btn ach-btn--grey" onClick={()=>nav("/machines")} data-testid="bih-view-machines">See the fleet →</button>
          </div>
        </div>
      )}
    </div>
  );
}
